import { useEffect, useRef, useState } from "react"
import useScrollTrigger from "./useScrollTrigger"

export interface useCountUpParameters {
  wrapperRef: any
  threshold?: number
  start?: number
  end: number
  duration?: number
  decimals?: number
  step?: number
}

const useCountUp: (params: useCountUpParameters) => string = ({
  wrapperRef,
  threshold = 0.25,
  start = 0,
  end,
  duration = 0.5,
  step = 1,
  decimals,
}) => {
  const timerRef = useRef<any>()
  const [hasTriggered, setHasTriggered] = useState(false)
  const [number, setNumber] = useState(start)
  const { isTriggered } = useScrollTrigger({ wrapperRef, threshold })

  useEffect(() => {
    if (hasTriggered) {
      setNumber(end)
      return
    }

    if (!isTriggered) return
    setHasTriggered(true)
    const interval = (duration * 1000) / ((end - start) / step)
    let tmpNum = start

    const timer = setInterval(() => {
      if (tmpNum >= end) return
      tmpNum += step
      setNumber(tmpNum)
    }, interval)
    timerRef.current = timer

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isTriggered, start, end, duration, step])

  useEffect(() => {
    if (!hasTriggered) return
    setTimeout(() => {
      setNumber(end)
      if (timerRef.current) clearInterval(timerRef.current)
    }, duration * 1000)
  }, [hasTriggered, end, duration])

  if (decimals || decimals === 0) return number.toFixed(decimals)

  const log10 = Math.log10(step)
  return number.toFixed(log10 > 0 ? 0 : Math.abs(Math.ceil(log10)))
}

export default useCountUp
